import React from 'react';
import { Button, Stack, Box, Heading, VStack, Text } from '@chakra-ui/react';
import {
  Control,
  FieldErrors,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormWatch,
} from 'react-hook-form';

import FormInput from '@/components/Inputs/FormInput';
import FormSelect from '@/components/Inputs/FormSelect';
import FormTextarea from '@/components/Inputs/FormTextarea';
import FormNumberInput from '@/components/Inputs/FormNumberInput';
import FormCheckbox from '@/components/Inputs/FormCheckbox';
import FormPersonnummerInput from '@/components/Inputs/FormPersonnummerInput';
import { IncomeType, RentalApplicationFormValues } from '@/types/rentalApplication';
import { CITY_OPTIONS } from '@/constants/cities';
import { User, Wallet, Home as HomeIcon, FileText } from 'lucide-react';

interface RentalApplicationFormProps {
  register: UseFormRegister<RentalApplicationFormValues>;
  control: Control<RentalApplicationFormValues>;
  watch: UseFormWatch<RentalApplicationFormValues>;
  errors: FieldErrors<RentalApplicationFormValues>;
  handleSubmit: UseFormHandleSubmit<RentalApplicationFormValues>;
  onSubmit: (data: RentalApplicationFormValues) => void;
  isLoading?: boolean;
}

const RentalApplicationForm: React.FC<RentalApplicationFormProps> = ({
  watch,
  control,
  register,
  errors,
  handleSubmit,
  onSubmit,
  isLoading = false,
}) => {
  const hasCurrentLandlord = watch('hasCurrentLandlord');

  const incomeTypeOptions = [
    { label: 'Arbete', value: IncomeType.WORK },
    { label: 'Studier', value: IncomeType.STUDIES },
    { label: 'Eget företag', value: IncomeType.OWN_COMPANY },
    { label: 'Pension', value: IncomeType.PENSION },
    { label: 'A-kassa', value: IncomeType.UNEMPLOYED },
    { label: 'Bidrag', value: IncomeType.ALLOWANCE },
    { label: 'Annat', value: IncomeType.OTHER },
  ];

  return (
    <Box maxW="4xl" mx="auto" w="100%">
      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack gap={{ base: '6', md: '8' }}>
          {/* Personal Information Section */}
          <Box
            w="100%"
            p={{ base: '6', md: '8' }}
            bg="bg"
            borderRadius="lg"
            borderWidth="1px"
            borderColor="border"
          >
            <VStack align="start" gap="6">
              <Box display="flex" alignItems="center" gap="3">
                <Box fontSize="2xl" color="blue.fg">
                  <User />
                </Box>
                <Heading size="lg" fontWeight="semibold">
                  Personlig information
                </Heading>
              </Box>

              <Stack w="100%" gap={4}>
                <Stack direction={['column', 'row']} gap={4}>
                  <FormInput
                    required
                    name="firstname"
                    label="Förnamn"
                    register={register}
                    errors={errors}
                  />

                  <FormInput
                    required
                    name="lastname"
                    label="Efternamn"
                    register={register}
                    errors={errors}
                  />
                </Stack>

                <FormPersonnummerInput
                  required
                  name="ssn"
                  label="Personnummer"
                  control={control}
                  errors={errors}
                  placeholder="ÅÅÅÅMMDD-XXXX"
                  helperText="12 siffror, t.ex. 19901231-1234"
                />

                <Stack direction={['column', 'row']} gap={4}>
                  <FormInput
                    required
                    name="phoneNumber"
                    label="Telefonnummer"
                    register={register}
                    errors={errors}
                  />

                  <FormInput
                    required
                    name="email"
                    label="E-postadress"
                    type="email"
                    register={register}
                    errors={errors}
                  />
                </Stack>

                <FormSelect
                  required
                  name="city"
                  label="Önskad stad"
                  control={control}
                  errors={errors}
                  options={CITY_OPTIONS}
                  placeholder="Välj stad"
                />
              </Stack>
            </VStack>
          </Box>

          {/* Financial Information Section */}
          <Box
            w="100%"
            p={{ base: '6', md: '8' }}
            bg="bg"
            borderRadius="lg"
            borderWidth="1px"
            borderColor="border"
          >
            <VStack align="start" gap="6">
              <Box display="flex" alignItems="center" gap="3">
                <Box fontSize="2xl" color="blue.fg">
                  <Wallet />
                </Box>
                <Heading size="lg" fontWeight="semibold">
                  Inkomst
                </Heading>
              </Box>

              <Stack w="100%" gap={4}>
                <Stack direction={['column', 'row']} gap={4}>
                  <FormNumberInput
                    required
                    name="incomeMonthly"
                    label="Månadsinkomst (kr)"
                    control={control}
                    errors={errors}
                    min={0}
                    step={1000}
                    helperText="Din bruttoinkomst per månad"
                  />

                  <FormSelect
                    required
                    name="incomeType"
                    label="Inkomstkälla"
                    control={control}
                    errors={errors}
                    options={incomeTypeOptions}
                    placeholder="Välj inkomstkälla"
                  />
                </Stack>

                <FormInput
                  required
                  name="employer"
                  label="Arbetsgivare"
                  register={register}
                  errors={errors}
                />
              </Stack>
            </VStack>
          </Box>

          {/* Current Housing Section */}
          <Box
            w="100%"
            p={{ base: '6', md: '8' }}
            bg="bg"
            borderRadius="lg"
            borderWidth="1px"
            borderColor="border"
          >
            <VStack align="start" gap="6">
              <Box display="flex" alignItems="center" gap="3">
                <Box fontSize="2xl" color="blue.fg">
                  <HomeIcon />
                </Box>
                <Heading size="lg" fontWeight="semibold">
                  Nuvarande boendesituation & Preferenser
                </Heading>
              </Box>

              <Stack w="100%" gap={4}>
                <FormCheckbox
                  name="hasCurrentLandlord"
                  label="Jag har en nuvarande hyresvärd"
                  control={control}
                  errors={errors}
                />

                {hasCurrentLandlord && (
                  <Stack direction={['column', 'row']} gap={4}>
                    <FormInput
                      required
                      name="landlordName"
                      label="Hyresvärdens namn"
                      register={register}
                      errors={errors}
                    />

                    <FormInput
                      required
                      name="landlordPhone"
                      label="Hyresvärdens telefon"
                      register={register}
                      errors={errors}
                    />
                  </Stack>
                )}

                <Stack direction={['column', 'row']} gap={4} pt={4}>
                  <FormNumberInput
                    required
                    name="rentMin"
                    label="Lägsta hyra (kr)"
                    control={control}
                    errors={errors}
                    min={0}
                    step={500}
                    helperText="Lägsta acceptabla hyra"
                  />

                  <FormNumberInput
                    required
                    name="rentMax"
                    label="Högsta hyra (kr)"
                    control={control}
                    errors={errors}
                    min={0}
                    step={500}
                    helperText="Högsta acceptabla hyra"
                  />
                </Stack>
              </Stack>
            </VStack>
          </Box>

          {/* Additional Information Section */}
          <Box
            w="100%"
            p={{ base: '6', md: '8' }}
            bg="bg"
            borderRadius="lg"
            borderWidth="1px"
            borderColor="border"
          >
            <VStack align="start" gap="6">
              <Box display="flex" alignItems="center" gap="3">
                <Box fontSize="2xl" color="blue.fg">
                  <FileText />
                </Box>
                <Heading size="lg" fontWeight="semibold">
                  Övrigt
                </Heading>
              </Box>

              <Stack w="100%" gap={4}>
                <FormCheckbox
                  name="smoker"
                  label="Jag är rökare"
                  control={control}
                  errors={errors}
                />

                <FormInput
                  name="pets"
                  label="Husdjur (beskriv)"
                  register={register}
                  errors={errors}
                  placeholder="Typ och antal husdjur"
                />

                <FormInput
                  name="children"
                  label="Barn (beskriv)"
                  register={register}
                  errors={errors}
                  placeholder="Antal och ålder på barn"
                />

                <FormTextarea
                  name="additionalInfo"
                  label="Ytterligare information"
                  register={register}
                  errors={errors}
                  helperText="Andra uppgifter du vill dela med dig av"
                  placeholder="Vänligen dela annan information som kan vara relevant för din ansökan"
                  rows={4}
                />
              </Stack>
            </VStack>
          </Box>

          {/* Submit Button */}
          <Button
            type="submit"
            colorPalette="blue"
            size={{ base: 'lg', md: 'xl' }}
            w={{ base: '100%', md: 'auto' }}
            px={{ base: '8', md: '12' }}
            loading={isLoading}
          >
            Skicka ansökan
          </Button>
          <Text fontSize="sm" color="fg.muted" maxW="lg" textAlign="center" mx="auto">
            Genom att skicka in din ansökan godkänner du till att Palatium Fastigheter AB sparar
            dina personuppgifter, och att Palatium Fastigheter AB kan komma att inhämta upplysningar
            från kreditupplysningsföretag, myndigheter och tidigare/nuvarande hyresvärd.
          </Text>
        </VStack>
      </form>
    </Box>
  );
};

export default RentalApplicationForm;
